import { getConfigValue } from "../api/price";
import { useOfferCalculatorDispatch, useOfferCalculatorState } from "../context/OfferCalculatorContext";
import { normalizeOption } from "../utils/configHelpers";
import { normalizePackagesMeta } from "../utils/packagesMeta";
import React, { useEffect, useMemo, useState } from "react";
import { ToggleButton, ToggleButtonGroup } from "react-aria-components";
const PACKAGES_DEBOUNCE_MS = 500;
const checkIcon = (<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path d="M11.8352 3.89074C12.0798 3.61555 12.5008 3.59079 12.776 3.8354C13.0512 4.08002 13.0759 4.50097 12.8313 4.77616L7.49798 10.7762C7.37478 10.9148 7.19965 10.9961 7.01426 11.0001C6.82878 11.004 6.64977 10.9303 6.52077 10.797L4.18744 8.38488C3.93158 8.12025 3.93849 7.69813 4.20306 7.44218C4.46765 7.18636 4.8898 7.19336 5.14577 7.4578L6.9791 9.35298L11.8352 3.89074Z" fill="currentColor"/>
  </svg>);
function PackageStatusLine({ isIncludedByDefault, isSelected, }) {
    if (isIncludedByDefault) {
        return (<span className="text-xs font-normal leading-[1.4rem] tracking-wide text-[#0071CE] whitespace-nowrap">
        w pakiecie
      </span>);
    }
    if (isSelected) {
        return (<span className="flex h-4 items-center justify-center text-[#0071CE]">
        {checkIcon}
      </span>);
    }
    return <span className="block h-4" aria-hidden/>;
}
function isPackageRequired(pkg, field) {
    var _a, _b;
    const option = (_a = field.options) === null || _a === void 0 ? void 0 : _a[pkg.id];
    return Boolean(((_b = option === null || option === void 0 ? void 0 : option.options) === null || _b === void 0 ? void 0 : _b.required) || pkg.required);
}
function buildPackagesFromKeys(keys, visiblePackages, field) {
    const next = {};
    for (const id of keys) {
        next[String(id)] = "1";
    }
    visiblePackages.forEach((pkg) => {
        if (isPackageRequired(pkg, field)) {
            next[pkg.id] = "1";
        }
    });
    return next;
}
function PackageToggleGroup({ visiblePackages, field, }) {
    const state = useOfferCalculatorState();
    const dispatch = useOfferCalculatorDispatch();
    const [localPackages, setLocalPackages] = useState(null);
    const activePackages = localPackages !== null && localPackages !== void 0 ? localPackages : state.packages;
    const selectedKeys = useMemo(() => new Set(Object.entries(activePackages)
        .filter(([, val]) => val === "1")
        .map(([id]) => id)), [activePackages]);
    // When state.packages is replaced externally (e.g. a price response), discard any
    // pending local selection so a stale toggle doesn't overwrite the server-backed state.
    useEffect(() => {
        setLocalPackages(null);
    }, [state.packages]);
    // Commit the pending local selection after the debounce delay. The cleanup
    // cancels any in-flight timer so rapid toggles coalesce into one dispatch.
    // When localPackages is null there's nothing to commit.
    useEffect(() => {
        if (localPackages === null) {
            return;
        }
        const id = setTimeout(() => {
            dispatch({ type: "set_param", name: "packages", value: localPackages });
            setLocalPackages(null);
        }, PACKAGES_DEBOUNCE_MS);
        return () => clearTimeout(id);
    }, [localPackages, dispatch]);
    return (<ToggleButtonGroup selectionMode="multiple" selectedKeys={selectedKeys} onSelectionChange={(keys) => {
            setLocalPackages(buildPackagesFromKeys(keys, visiblePackages, field));
        }} className="notranslate grid gap-2 grid-cols-4">
      {visiblePackages.map((pkg) => {
            const isIncludedByDefault = isPackageRequired(pkg, field);
            const isSelected = isIncludedByDefault || selectedKeys.has(pkg.id);
            return (<ToggleButton key={pkg.id} id={pkg.id} isDisabled={isIncludedByDefault} className={({ isSelected: selected }) => `flex flex-col items-center gap-1 rounded-lg border p-2 outline-none transition focus-visible:ring-2 focus-visible:ring-[#0071CE] ${selected || isIncludedByDefault
                    ? "border-[#0071CE] bg-[#ECF5FE] text-[#0071CE]"
                    : "border-[#DDE2E4] text-[#66676C]"}`}>
            <span className={`flex h-8 w-8 items-center justify-center [&_svg]:h-5 [&_svg]:w-5 ${isSelected ? "" : "text-black [&_path]:fill-current"}`} dangerouslySetInnerHTML={{ __html: pkg.icon }}/>
            <span className={`text-center text-xs leading-[1.4rem] tracking-wide ${isSelected ? "font-bold" : "font-normal"}`}>
              {pkg.name}
            </span>
            <PackageStatusLine isIncludedByDefault={isIncludedByDefault} isSelected={isSelected}/>
          </ToggleButton>);
        })}
    </ToggleButtonGroup>);
}
export default function ServicePackageToggles() {
    const state = useOfferCalculatorState();
    const field = getConfigValue(state.config, "packages");
    if (!field || field.options === null) {
        return null;
    }
    const visiblePackages = normalizePackagesMeta(state.bootstrap.packagesMeta).filter((pkg) => {
        var _a;
        const option = (_a = field.options) === null || _a === void 0 ? void 0 : _a[pkg.id];
        if (!option)
            return false;
        const normalized = normalizeOption(pkg.id, option);
        return normalized !== null;
    });
    if (!visiblePackages.length) {
        return null;
    }
    return (<>
      <hr className="border-t border-[#DDE2E4]"/>
      <div className="config-group group-packages">
        <div className="mb-4 text-base font-medium leading-5">
          Dodaj usługi do pakietu
        </div>
        <PackageToggleGroup visiblePackages={visiblePackages} field={field}/>
      </div>
    </>);
}
